/**
 * Toast notification system.
 * Slides in from top, auto-dismisses, fully accessible with aria-live.
 */

import { t } from '../../i18n';

export type NotificationType = 'info' | 'success' | 'warning' | 'error';

const DEFAULT_DURATION = 4000; // ms

let container: HTMLElement | null = null;

/**
 * Ensures the notification container exists in the DOM.
 * Created once, reused for all notifications.
 */
function getContainer(): HTMLElement {
  if (container && document.body.contains(container)) {
    return container;
  }

  container = document.createElement('div');
  container.className = 'notification-container';
  container.setAttribute('aria-label', t('notify.notifications'));
  container.setAttribute('role', 'status');
  document.body.appendChild(container);
  return container;
}

/**
 * Icon map for notification types (Unicode, no library needed).
 */
const ICONS: Record<NotificationType, string> = {
  info: '\u2139\uFE0F',     // information
  success: '\u2705',         // check mark
  warning: '\u26A0\uFE0F',  // warning sign
  error: '\u274C',           // cross mark
};

/**
 * Show a toast notification.
 *
 * @param message  - Text to display
 * @param type     - Visual style: info, success, warning, error
 * @param duration - Auto-dismiss time in ms (0 = no auto-dismiss)
 */
export function showNotification(
  message: string,
  type: NotificationType = 'info',
  duration: number = DEFAULT_DURATION,
): void {
  const wrapper = getContainer();

  const toast = document.createElement('div');
  toast.className = `notification-toast ${type}`;
  toast.setAttribute('role', 'alert');
  toast.setAttribute('aria-live', 'assertive');
  toast.setAttribute('aria-atomic', 'true');

  // Icon
  const icon = document.createElement('span');
  icon.setAttribute('aria-hidden', 'true');
  icon.textContent = ICONS[type];
  icon.style.cssText = 'font-size: 22px; flex-shrink: 0; line-height: 1;';
  toast.appendChild(icon);

  // Message text
  const text = document.createElement('span');
  text.textContent = message;
  text.style.cssText = 'flex: 1; min-width: 0;';
  toast.appendChild(text);

  // Dismiss button
  const dismissBtn = document.createElement('button');
  dismissBtn.setAttribute('aria-label', t('notify.dismiss'));
  dismissBtn.style.cssText = `
    background: none;
    border: none;
    color: inherit;
    font-size: 20px;
    cursor: pointer;
    padding: 4px;
    line-height: 1;
    flex-shrink: 0;
    min-width: 44px;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  `;
  dismissBtn.textContent = '\u2715'; // multiplication sign (X)

  function dismiss(): void {
    toast.classList.add('dismissing');
    toast.addEventListener('animationend', () => {
      toast.remove();
    }, { once: true });
  }

  dismissBtn.addEventListener('click', dismiss);
  toast.appendChild(dismissBtn);

  wrapper.appendChild(toast);

  // Auto-dismiss
  if (duration > 0) {
    setTimeout(dismiss, duration);
  }
}
